import { getApiUri } from '@carlito/config';
import {
  ControllerExecutionResult,
  DomainError,
  IController,
  UseCaseDomainErrors,
  UseCaseError,
} from '@carlito/ddd';
import { SupportedLanguages } from '@carlito/intl';
import { onJwtChanged } from '../hooks';

const BOOKITO_API_URL = getApiUri('carlito_federation');

type BookitRequestOptions =
  | ({
      queryParameters?: Record<string, string>;
    } & RequestInit)
  | undefined;

let jwt: string | undefined;
let language: SupportedLanguages | undefined;

export function setBookitoApiLanguage(newLanguage?: SupportedLanguages) {
  const isSetFirstTime = !language;
  language = newLanguage;

  return { isSetFirstTime };
}

export function setBookitoApiJwt(newJwt?: string) {
  if (newJwt !== jwt) {
    onJwtChanged();
  }
  jwt = newJwt;
}

function cleanQueryParameters(queryParameters?: Record<string, string>) {
  if (!queryParameters) {
    return undefined;
  }

  const cleanedQueryParameters: Record<string, string> = Object.entries(queryParameters).reduce(
    (acc, [key, value]) => {
      if (value) {
        return {
          ...acc,
          [key]: value,
        };
      }

      return acc;
    },
    {}
  );

  return cleanedQueryParameters;
}

export async function bookitoRequest<TController extends IController>(
  endpoint: string,
  options?: BookitRequestOptions
) {
  const { queryParameters, ...fetchOpts } = options || {};

  const languageHeader: HeadersInit = language ? { 'Accept-Language': language } : {};

  const response = await fetch(
    `${BOOKITO_API_URL}${endpoint}${
      queryParameters ? `?${new URLSearchParams(cleanQueryParameters(queryParameters))}` : ''
    }`,
    {
      ...fetchOpts,
      headers: {
        ...(fetchOpts.headers || {}),
        Authorization: jwt ? `Bearer ${jwt}` : '',
        ...languageHeader,
      },
    }
  );

  const reqBody = (await response.json()) as ControllerExecutionResult<TController>;

  if (reqBody.type !== 'success') {
    switch (reqBody.type) {
      case 'domain_error': {
        const domainErrors = reqBody.errors.map((e) => {
          const domainError = new DomainError({
            errorCode: e.code,
            message: e.message,
          });
          return domainError;
        });
        throw new UseCaseDomainErrors(domainErrors);
      }

      case 'use_case_error': {
        const error = new UseCaseError({
          errorCode: reqBody.errors[0].code,
          message: reqBody.errors[0].message,
        });
        throw error;
      }

      case 'controller_error':
      default:
        throw new Error('Unexpected error');
    }
  }

  if (!response.ok) {
    throw new Error('Unexpected error');
  }

  // Force success type, because we already checked it above;
  // an error would have been thrown otherwise
  type SuccessBody = Extract<ControllerExecutionResult<TController>, { type: 'success' }>;

  return reqBody as SuccessBody;
}
